import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Loading, Tab, TabList, Tabs } from "@carbon/react";
import { useCompanyProfile } from "../../hooks/useCompany";
import { useUser } from "../../context";
import { CompletionPercentage } from "../../components";
import { ABILITIES } from "../../utils";
import EmptyState from "../../components/carbon/EmptyState/EmptyState";
import CompanyLogo from "../../components/CompanyProfile/CompanyLogo";
import OverviewTab from "./tabs/OverviewTab";
import CompanyDataTab from "./tabs/CompanyDataTab";
import ContactsTab from "./tabs/ContactsTab";
import MainClientsTab from "./tabs/MainClientsTab";
import "./companyProfile.scss";

/**
 * The company profile VIEWER (`/company`), rebuilt on Carbon (Task 6, Profile/Company flow) in
 * place of 642 lines of Tailwind. Gated on `SELLER_VIEW_COMPANY` (`if (!can(...)) return null`) and read-only — all
 * edits happen in the wizard at `/company/edit` (`CompanyProfileForm`, a separate container),
 * unchanged by this task.
 *
 * Split, per the brief, into one file per pre-Carbon inline tab component under `tabs/`
 * (`OverviewTab`, `CompanyDataTab` — itself composing `AddressesTab`/`BankingTab`/
 * `CertificationsTab`/`FilesTab` exactly as the original nested them — `ContactsTab`,
 * `MainClientsTab`, plus the `InfoCard`/`StatsCard` presentational helpers), and the pure
 * `fileText`/`getContactTypeLabel` formatters into `companyProfileHelpers.ts` (unit-tested
 * separately, see `src/__tests__/containers/CompanyProfile/companyProfileHelpers.test.ts`).
 *
 * Tabs: Carbon `Tabs`/`TabList`/`Tab` drive `selectedTab`, the same pattern `Tags.tsx`/
 * `Users.tsx`/`components/Material/Material.tsx` already use — a bare `TabList` for the switcher,
 * the tab body rendered manually below rather than through `TabPanels` (there is no `TabPanels`
 * precedent anywhere else in this codebase to mirror).
 *
 * `CompletionPercentage` (the header card with the company name, website/LinkedIn links, "Edit
 * Profile" button and completion bar) is a separate, still-Tailwind shared component — also
 * rendered by `Home/Seller/Seller.tsx` — and out of this task's scope; mounted here unchanged.
 *
 * Empty-state CTA: the original navigated to `/company/upload`, which Task 4's route redirect now
 * sends to `/company/edit` anyway. This version points directly at `/company/edit`.
 */
const CompanyProfile = () => {
  const { user, can } = useUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);

  const companyId = user?.company?.id || "";

  const { data: companyProfile, isLoading } = useCompanyProfile(companyId);

  if (!can(ABILITIES.SELLER_VIEW_COMPANY)) {
    return null;
  }

  if (isLoading) {
    return <Loading withOverlay description={t("cta.loading")} />;
  }

  if (!companyProfile) {
    return (
      <EmptyState
        title={t("companyProfile.noProfile")}
        description={t("companyProfile.createProfileDescription")}
        action={
          <Button onClick={() => navigate("/company/edit")}>
            {t("companyProfile.createProfile")}
          </Button>
        }
      />
    );
  }

  return (
    <div>
      <CompanyLogo
        fileKey={companyProfile.imageURL}
        name={companyProfile.commercialName}
      />
      <CompletionPercentage />

      <div className="company-profile__tabs">
        <Tabs
          selectedIndex={selectedTab}
          onChange={({ selectedIndex }) => setSelectedTab(selectedIndex)}
        >
          <TabList aria-label={t("companyProfile.title")}>
            <Tab>{t("companyProfile.basicInformation")}</Tab>
            <Tab>{t("companyProfile.companyData")}</Tab>
            <Tab>{t("companyProfile.contacts")}</Tab>
            <Tab>{t("companyProfile.mainClients")}</Tab>
          </TabList>
        </Tabs>
      </div>

      <div className="company-profile__content">
        {selectedTab === 0 && <OverviewTab companyProfile={companyProfile} />}
        {selectedTab === 1 && <CompanyDataTab companyProfile={companyProfile} />}
        {selectedTab === 2 && <ContactsTab companyProfile={companyProfile} />}
        {selectedTab === 3 && <MainClientsTab companyProfile={companyProfile} />}
      </div>
    </div>
  );
};

export default CompanyProfile;
